// PortInTab.jsx
//
// Phase A scope (carried forward): render the eligibility coach card when the
// tenant is not yet eligible.
// Phase B Slice B2 scope (this file): when eligible, fetch existing draft +
// in-flight port_requests via `list`, render them as PortRequestCard rows, and
// expose a "Start New Port" CTA that opens the PortInWizard.

import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, ArrowLeftRight, AlertCircle } from 'lucide-react';
import EligibilityGate from './EligibilityGate';
import PortRequestCard from './PortRequestCard';
import PortInWizard from './PortInWizard';
import PortRequestDetail from './PortRequestDetail';
import { useAuth } from '../../../../../context/AuthContext';
import { PERMISSIONS } from '../../../../../lib/permissions';
import { createDraft, listPortRequests, cancelPortRequest } from '../../../../../lib/portInClient';

export default function PortInTab({ eligibility, onUpdate }) {
  const { hasPermission } = useAuth();
  const canInitiate = hasPermission(PERMISSIONS.INITIATE_PORT_REQUEST);

  const [portRequests, setPortRequests] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);

  const [openWizardId, setOpenWizardId] = useState(null);
  const [openDetailId, setOpenDetailId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const refresh = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    const { data, error } = await listPortRequests({ limit: 50 });
    if (error) {
      setListError(error.message || 'Failed to load port requests.');
      setListLoading(false);
      return;
    }
    // Hide canceled rows from the list. The row still exists in the DB
    // (audit trail + port_request_events), but a discarded port should not
    // clutter the customer's active view.
    const all = Array.isArray(data?.portRequests) ? data.portRequests : [];
    setPortRequests(all.filter((p) => p.status !== 'canceled'));
    setListLoading(false);
  }, []);

  useEffect(() => {
    if (eligibility?.eligible) refresh();
  }, [eligibility?.eligible, refresh]);

  if (!eligibility) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  if (!eligibility.eligible) {
    return <EligibilityGate eligibility={eligibility} />;
  }

  const handleStartNew = async () => {
    setCreating(true);
    setCreateError(null);
    const { data, error } = await createDraft();
    setCreating(false);
    if (error) {
      setCreateError(error.message || 'Failed to start a new port draft.');
      return;
    }
    const id = data?.portRequest?.id;
    if (!id) {
      setCreateError('Draft created but no ID returned. Please refresh.');
      return;
    }
    setOpenWizardId(id);
    refresh();
  };

  const handleClose = async () => {
    setOpenWizardId(null);
    refresh();
    onUpdate?.();
  };

  const handleDetailClose = () => {
    setOpenDetailId(null);
    refresh();
    onUpdate?.();
  };

  /**
   * Discard a port_request. Calls the cancel dispatcher action (which
   * cancels the Twilio PortIn if one exists, transitions the row to
   * status='canceled', and cleans up Vault secrets), then optimistically
   * removes the row from the local list so it disappears immediately. A
   * background refresh re-syncs from the server.
   *
   * Returns a string on failure so PortRequestCard can surface the message
   * inline; returns null on success.
   */
  const handleDiscard = async (portRequest) => {
    const { data, error } = await cancelPortRequest(
      portRequest.id,
      'Customer discarded the in-progress port from the list.',
    );
    if (error) {
      let body = null;
      try {
        if (error.context && typeof error.context.clone === 'function') {
          const bodyText = await error.context.clone().text();
          if (bodyText) {
            try { body = JSON.parse(bodyText); } catch { body = { error: bodyText }; }
          }
        } else if (typeof error.context?.body === 'string') {
          try { body = JSON.parse(error.context.body); } catch { body = { error: error.context.body }; }
        }
      } catch { /* ignore */ }
      return body?.error || error.message || 'Could not discard this port request.';
    }
    if (data?.success || data?.alreadyCanceled) {
      // Optimistic removal so the row disappears immediately. The refresh
      // below re-syncs the canonical list (canceled rows are filtered out
      // by the parent list view's terminal-status check, if applicable).
      setPortRequests((prev) => prev.filter((p) => p.id !== portRequest.id));
      refresh();
      onUpdate?.();
      return null;
    }
    return 'Cancel returned an unexpected response.';
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-5">
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            Bring your existing numbers into SurFox
          </h3>
          <p className="text-sm text-slate-500 mt-0.5 max-w-xl">
            Port your established business numbers to SurFox without losing
            service. Numbers stay live throughout the entire process.
          </p>
        </div>
        {canInitiate && (
          <button
            type="button"
            onClick={handleStartNew}
            disabled={creating}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Start new port
          </button>
        )}
      </div>

      {createError && (
        <div className="mb-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{createError}</span>
        </div>
      )}

      {listLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
        </div>
      )}

      {!listLoading && listError && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{listError}</span>
        </div>
      )}

      {!listLoading && !listError && portRequests.length === 0 && (
        <EmptyState onStart={canInitiate ? handleStartNew : null} creating={creating} />
      )}

      {!listLoading && !listError && portRequests.length > 0 && (
        <div className="space-y-3">
          {portRequests.map((pr) => (
            <PortRequestCard
              key={pr.id}
              portRequest={pr}
              onDiscard={handleDiscard}
              onOpen={(opened) => {
                // Pre-submit rows route back into the wizard so the customer
                // can finish the missing piece (signing, bill upload, or
                // hitting Submit). The wizard auto-jumps to the right step
                // based on draft.status.
                //
                // Phase E: everything past submitted routes to the
                // PortRequestDetail timeline view (status transitions,
                // per-number outcomes, exception + resubmit, cancel).
                const WIZARD_STATUSES = new Set([
                  'draft',
                  'awaiting_signature',
                  'awaiting_documents',
                  'ready_to_submit',
                ]);
                if (WIZARD_STATUSES.has(opened.status)) {
                  setOpenWizardId(opened.id);
                  setOpenDetailId(null);
                } else {
                  setOpenWizardId(null);
                  setOpenDetailId(opened.id);
                }
              }}
            />
          ))}
        </div>
      )}

      {openWizardId && (
        <PortInWizard portRequestId={openWizardId} onClose={handleClose} />
      )}

      {openDetailId && (
        <PortRequestDetail
          portRequestId={openDetailId}
          onClose={handleDetailClose}
          onChange={refresh}
        />
      )}
    </div>
  );
}

function EmptyState({ onStart, creating }) {
  return (
    <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-200">
      <ArrowLeftRight className="w-10 h-10 text-slate-300 mx-auto mb-3" />
      <p className="text-slate-700 font-medium">No port requests yet</p>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
        Start a new port to bring an existing business number into SurFox.
        We'll walk you through it step by step.
      </p>
      {onStart && (
        <button
          type="button"
          onClick={onStart}
          disabled={creating}
          className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Start your first port
        </button>
      )}
    </div>
  );
}
